import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, BookOpen } from 'lucide-react';
type Section = {
  heading?: string;
  bullets: string[];
};
type Chapter = {
  emoji: string;
  title: string;
  subtitle: string;
  sections: Section[];
};
const chaptersData: Chapter[] = [{
  emoji: '⏱️',
  title: 'Parte 1 · La guardia en tiempo real',
  subtitle: 'Lo que pasa cuando no hay tiempo para pensar',
  sections: [{
    heading: '1.1 — Signos que no podés ignorar',
    bullets: ['El paciente que "no se ve bien": cómo leerlo antes de que el médico llegue', 'Cambios de estado que te tienen que encender una alarma interna', 'La diferencia entre "estable pero cagado" y "estable pero en riesgo real"']
  }, {
    heading: '1.2 — Urgencias que van a caer en tus manos',
    bullets: ['Crisis hipertensiva: qué hacés mientras llega el médico', 'Hipoglucemia: del reconocimiento a la glucosa en 3 pasos', 'Convulsión activa: posición, tiempo, registro', 'Disnea súbita: cómo diferenciar la posta del ataque de pánico', 'Dolor torácico: el protocolo de los primeros 10 minutos', 'Paciente que no responde: RCP básico con el chequeo previo que siempre se saltea']
  }, {
    heading: '1.3 — RCP actualizado sin letra chica',
    bullets: ['Secuencia BLS adulto / pediátrico lado a lado', 'Cuándo parar (y cómo documentarlo)', 'DEA: pasos exactos para cuando nunca lo usaste pero tenés que usarlo']
  }]
}, {
  emoji: '💉',
  title: 'Parte 2 · Procedimientos de todos los días',
  subtitle: 'Hacelo bien, documentalo mejor',
  sections: [{
    heading: '2.1 — Vías y accesos',
    bullets: ['Canalización de vía periférica: sitios, calibres, qué poner en la historia', 'Cuidado del catéter venoso central (CVC): lo que no te enseñaron en el instituto', 'Sondas vesicales: técnica estéril sin vuelta, en hombre y en mujer', 'Sonda nasogástrica: colocación, verificación, y por qué el aire en la boca del estómago no alcanza']
  }, {
    heading: '2.2 — Administración de medicamentos',
    bullets: ['Cálculo de goteo a mano: la fórmula que funciona aunque no tenés bomba', 'Mezclas incompatibles más comunes (tabla de un vistazo)', 'La regla de los 5 correctos, actualizada a los 9 correctos', 'Insulina: tipos, jeringas, técnica subcutánea correcta', 'Antibióticos IV: dilución, tiempo de infusión, qué hacer si el paciente se pone raro']
  }, {
    heading: '2.3 — Curaciones y heridas',
    bullets: ['Úlceras por presión: clasificación con foto mental + qué poner en cada estadio', 'Herida quirúrgica normal vs. infectada: la diferencia en 3 signos', 'Curaciones húmedas vs. secas: cuándo cada una', 'Desbridamiento básico: qué podés hacer vos y qué esperar al médico']
  }, {
    heading: '2.4 — Oxigenoterapia',
    bullets: ['Dispositivos y flujos: cuándo cánula, cuándo máscara, cuándo reservorio', 'Saturación: los números reales (no los del libro) según patología', 'Aspiración de secreciones: la técnica que no hace daño']
  }]
}, {
  emoji: '🛏️',
  title: 'Parte 3 · El paciente según dónde está',
  subtitle: 'Sala general, UTI, guardia, CAPS',
  sections: [{
    heading: '3.1 — Paciente en sala general',
    bullets: ['Monitoreo por turno: qué mirás, qué anotás, qué reportás', 'El informe de turno que no deja huecos (estructura real)', 'Posicionamiento antiúlcera: el cronograma que se puede pegar en la cama', 'Prevención de caídas: evaluación de Morse simplificada']
  }, {
    heading: '3.2 — Paciente crítico / UTI',
    bullets: ['Lectura básica del monitor: PVC, PAM, FC, SpO2 juntos', 'El ventilador mecánico: alarmas que tenés que saber silenciar y cuáles no', 'Sedoanalgesia: escalas de Richmond y Campbell en una sola hoja', 'Destete ventilatorio: señales de que el paciente está listo']
  }, {
    heading: '3.3 — Paciente pediátrico',
    bullets: ['Dosis por peso: tabla rápida de los fármacos más frecuentes', 'Vías de acceso en el nene difícil', 'Fiebre en pediatría: antitérmicos, tiempos, cuándo te preocupás']
  }, {
    heading: '3.4 — Paciente adulto mayor',
    bullets: ['Los síndromes geriátricos que se presentan distinto en la vejez', 'Delirium: cómo diferenciarlo de la demencia con el mini-test de urgencia', 'Polifarmacia: los grupos que más interaccionan']
  }, {
    heading: '3.5 — Paciente obstétrica',
    bullets: ['Control prenatal básico en CAPS: tensión, peso, altura uterina', 'Parto inminente sin obstetra: los pasos que no podés no saber', 'Postparto inmediato: sangrado, útero, signos de alarma']
  }]
}, {
  emoji: '⚠️',
  title: 'Parte 4 · Lo que siempre se complica',
  subtitle: 'Situaciones que nadie explica hasta que las vivís',
  sections: [{
    heading: '4.1 — El médico no contesta y el paciente empeora',
    bullets: ['Cómo comunicar urgencia sin sonar histérico', 'Qué podés resolver solo y qué definitivamente no', 'Cómo cubrirte cuando tomás una decisión de enfermería independiente']
  }, {
    heading: '4.2 — Familia en estado de guerra',
    bullets: ['Cómo dar información sin comprometerte legalmente', 'El límite entre contener y ceder', 'Cuándo llamar al jefe de guardia o al médico de cabecera']
  }, {
    heading: '4.3 — El error de medicación',
    bullets: ['Qué hacés en los primeros 5 minutos', 'Cómo lo reportás sin incriminarte', 'El registro correcto en la historia clínica']
  }, {
    heading: '4.4 — El paciente que se quiere ir de alta voluntaria',
    bullets: ['Pasos legales: el formulario, el médico, el registro', 'Qué pasa si se va sin firmar']
  }, {
    heading: '4.5 — Paciente con conducta alterada / agresivo',
    bullets: ['Contención verbal antes de la física', 'Sujeción terapéutica: indicación, técnica, tiempo máximo, documentación', 'Protocolo de alerta de seguridad']
  }]
}, {
  emoji: '💊',
  title: 'Parte 5 · Farmacología en la práctica',
  subtitle: 'Sin la teoría que nunca usás',
  sections: [{
    heading: '5.1 — Los fármacos que más vas a usar',
    bullets: ['Tabla por droga: indicación / dosis adulto / dosis pediátrica / vía / tiempo de infusión / efectos adversos a vigilar', 'Adrenalina · Atropina · Furosemida · Metoclopramida · Ketorolac', 'Morfina / Tramadol · Midazolam · Diazepam · Hidrocortisona · Salbutamol', 'Metformina / Insulina NPH y corriente', 'Amoxicilina / Ampicilina-Sulbactam / Ciprofloxacina', 'Heparina (profiláctica y terapéutica)', 'Soluciones: SF, Ringer, dextrosa al 5% y al 10%']
  }, {
    heading: '5.2 — Las incompatibilidades que más matan líneas',
    bullets: ['Tabla de doble entrada: las 15 combinaciones que aparecen siempre']
  }, {
    heading: '5.3 — Diluciones paso a paso',
    bullets: ['Cómo preparar una ampolla de 500 mg para pasar en 100 cc', 'Concentraciones habituales por fármaco']
  }]
}, {
  emoji: '📋',
  title: 'Parte 6 · Planillas y registros',
  subtitle: 'Lo que firmás te salva o te hunde',
  sections: [{
    bullets: ['Planilla 1 — Control de signos vitales por turno (FC / TA / FR / Temp / SpO2 / Diuresis / Observaciones), lista para imprimir', 'Planilla 2 — Balance hídrico de 24 horas: ingresos vs egresos, hora por hora con totales', 'Planilla 3 — Hoja de administración de medicamentos (kardex): droga / dosis / vía / horario / firma', 'Planilla 4 — Valoración de riesgo de caídas (Morse simplificado): puntuación + intervenciones', 'Planilla 5 — Valoración de úlceras por presión (Braden): interpretación y plan de cuidados', 'Planilla 6 — Escala de dolor (EVA + FLACC para el que no habla)', 'Planilla 7 — Informe de turno estructurado que cualquier compañero entiende en 30 segundos', 'Planilla 8 — Registro de evento adverso / error de medicación', 'Planilla 9 — Consentimiento informado para procedimientos de enfermería', 'Planilla 10 — Checklist de egreso del paciente']
  }]
}, {
  emoji: '📊',
  title: 'Parte 7 · Infografías y material visual',
  subtitle: 'Lo que pegás en la pared del office',
  sections: [{
    bullets: ['Infografía 1 — RCP adulto paso a paso (secuencia visual con íconos)', 'Infografía 2 — Escala de Glasgow (ocular / verbal / motora)', 'Infografía 3 — Signos vitales normales por edad (de neonato a adulto mayor)', 'Infografía 4 — Sitios de inyección IM, SC e ID (esquema corporal)', 'Infografía 5 — Mapa de catéteres y calibres (color → calibre → uso → flujo)', 'Infografía 6 — Estadios de úlcera por presión (descripción + intervención)', 'Infografía 7 — Algoritmo de manejo de la fiebre (adulto y pediátrico)', 'Infografía 8 — Las 5 manos del lavado de manos (esquema OMS adaptado)', 'Infografía 9 — Compatibilidad de soluciones IV (tabla de colores)', 'Infografía 10 — ¿Cuándo llamar al médico? (árbol de decisión con 10 escenarios)', '⚠️ Todos los materiales son esquemas originales, iconográficos o de texto estructurado (sin fotos clínicas).']
  }]
}, {
  emoji: '🧠',
  title: 'Parte 8 · Autocuidado del enfermero',
  subtitle: 'Porque si te rompés vos, no cuidás a nadie',
  sections: [{
    bullets: ['Manejo del turno de 12 horas sin colapsar', 'El golpe con objeto cortante: qué hacés en los próximos 15 minutos', 'Síndrome de burnout en enfermería: señales tempranas y dónde pedir ayuda', 'Derechos laborales básicos: lo que podés y no podés negarte a hacer']
  }]
}];
export function Chapters() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="py-20 bg-pastel-50 px-4" data-id="element-41">
      <div className="max-w-3xl mx-auto" data-id="element-42">
        <div className="text-center mb-12" data-id="element-43">
          <div className="inline-flex items-center justify-center p-3 bg-navy-800 text-white rounded-full mb-4" data-id="element-44">
            <BookOpen size={24} data-id="element-45" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-navy-900 mb-4 max-w-2xl mx-auto leading-snug" data-id="element-46">
            📚 El manual completo para resolver la guardia sin depender de nadie
          </h2>
          <p className="text-navy-700" data-id="element-47">
            Todo lo que necesitás, organizado para consulta rápida en el momento
            exacto.
          </p>
        </div>

        <div className="space-y-3" data-id="element-48">
          {chaptersData.map((chapter, index) => {
          const isOpen = openIndex === index;
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.04
          }} className={`rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all border-2 ${isOpen ? 'bg-navy-800 border-navy-800' : 'bg-white border-pastel-200'}`} data-id="element-49">
                <button onClick={() => setOpenIndex(isOpen ? null : index)} className="w-full px-5 py-4 flex items-center justify-between text-left focus:outline-none" data-id="element-50">
                  <span className="flex items-center gap-3 pr-4" data-id="element-51">
                    <span className="text-2xl leading-none" data-id="element-52">
                      {chapter.emoji}
                    </span>
                    <span className="min-w-0" data-id="element-53">
                      <span className={`block font-bold transition-colors ${isOpen ? 'text-white' : 'text-navy-900'}`} data-id="element-54">
                        {chapter.title}
                      </span>
                      <span className={`block text-xs italic transition-colors ${isOpen ? 'text-pastel-200' : 'text-navy-500'}`} data-id="element-55">
                        {chapter.subtitle}
                      </span>
                    </span>
                  </span>
                  <motion.div animate={{
                rotate: isOpen ? 180 : 0
              }} transition={{
                duration: 0.2
              }} className={`shrink-0 transition-colors ${isOpen ? 'text-white' : 'text-navy-500'}`} data-id="element-56">
                    <ChevronDown size={20} data-id="element-57" />
                  </motion.div>
                </button>

                <AnimatePresence data-id="element-58">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} data-id="element-59">
                      <div className="px-5 pb-5 pt-1 border-t border-navy-700 space-y-4 mt-3" data-id="element-60">
                        {chapter.sections.map((section, si) => <div key={si} data-id="element-61">
                            {section.heading && <p className="text-gold-400 font-semibold text-sm mb-2" data-id="element-62">
                                {section.heading}
                              </p>}
                            <ul className="space-y-2" data-id="element-63">
                              {section.bullets.map((item, i) => <li key={i} className="flex items-start gap-2 text-pastel-100 text-sm" data-id="element-64">
                                  <span className="text-gold-400 mt-0.5" data-id="element-65">
                                    ✓
                                  </span>
                                  <span data-id="element-66">{item}</span>
                                </li>)}
                            </ul>
                          </div>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}